import { FormEvent, useEffect, useState, type CSSProperties } from "react";
import { useApp } from "@/contexts/AppContext";
import { CheckCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { Website } from "../WebsiteManagement";
import { WebsiteFormFields } from "../website-form/WebsiteFormFields";
import { WebsiteFormCard } from "../website-form/WebsiteFormCard";
import { WebsiteFormConfirmationDialog } from "../website-form/WebsiteFormConfirmationDialog";
import {
  type WebsiteFormData,
  type WebsiteFormValidationOptions,
} from "@/utils/websiteFormUtils";
import { useWebsiteFormSubmit } from "../website-form/useWebsiteFormSubmit";
import {
  WEBSITE_FORM_LIMITS,
  buildAddWebsiteFormConfig,
  createEmptyWebsiteFormData,
} from "../website-form/WebsiteFormConfig";
import { buildAddWebsitePayload } from "../website-form/WebsiteFormPayload";
import { getAiCategorySuggestionEnabled } from "@/services/publicSystemSettings.service";

type UrlErrorKey = string;

const formatSummaryValue = (value?: string | null) => {
  if (value === null || value === undefined) return "—";
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : "—";
};

interface SummaryEntryProps {
  label: string;
  value: string;
  valueStyle?: CSSProperties;
}

function SummaryEntry({ label, value, valueStyle }: SummaryEntryProps) {
  return (
    <div>
      <p className="text-xs text-secondary">{label}</p>
      <p className="text-sm break-words" style={valueStyle}>
        {value}
      </p>
    </div>
  );
}

interface AddWebsitePanelProps {
  onAddWebsite: (
    website: Omit<Website, "id" | "addedAt" | "addedBy">
  ) => Promise<void>;
  navigate: (page: string) => void;
}

export function AddWebsitePanel({
  onAddWebsite,
  navigate,
}: AddWebsitePanelProps) {
  const { t, language } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const successIconStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 18%, var(--background))",
    border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--background))",
  } as const;
  const summaryCardStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--surface) 70%, var(--background))",
    borderColor:
      "color-mix(in srgb, var(--foreground) 12%, var(--background))",
  } as const;
  const errorTextStyle = {
    color: "color-mix(in srgb, var(--accent) 82%, var(--foreground))",
  } as const;

  const [formData, setFormData] = useState<WebsiteFormData>(
    createEmptyWebsiteFormData()
  );
  const [categorizationResetKey, setCategorizationResetKey] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [addedWebsite, setAddedWebsite] = useState<Omit<
    Website,
    "id" | "addedAt" | "addedBy"
  > | null>(null);

  const [fieldErrors, setFieldErrors] = useState({
    title: false,
    url: false,
    description: false,
  });
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [urlErrorKey, setUrlErrorKey] = useState<UrlErrorKey | null>(null);
  const [aiFeatureEnabled, setAiFeatureEnabled] = useState(true);

  const submitWithValidation = useWebsiteFormSubmit({
    t,
    isSubmitting,
    setIsSubmitting,
    setShowValidation,
    setFieldErrors,
    setUrlErrorKey,
  });
  const formConfig = buildAddWebsiteFormConfig(
    t,
    <Plus className="w-4 h-4" />
  );

  useEffect(() => {
    let isMounted = true;
    const loadAiSetting = async () => {
      const enabled = await getAiCategorySuggestionEnabled();
      if (isMounted) setAiFeatureEnabled(enabled);
    };
    loadAiSetting();
    return () => {
      isMounted = false;
    };
  }, []);

  const hasZip = /^\d{5}$/.test(formData.zipCode.trim());
  const categoryMissing = showValidation && !formData.category1Id;
  const regionMissing = showValidation && !formData.regionId && !hasZip;

  const handleAddWebsite = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const validationOptions: WebsiteFormValidationOptions = {
        formData,
        ids: formConfig.validation.ids,
        max: WEBSITE_FORM_LIMITS,
        required: formConfig.validation.required,
        errorKeys: formConfig.validation.errorKeys,
      };

      await submitWithValidation({
        formData,
        validationOptions,
        onValidSubmit: async (normalizedUrl) => {
          const websiteData = buildAddWebsitePayload(
            formData,
            normalizedUrl
          );

          try {
            await onAddWebsite(websiteData);
            setAddedWebsite(websiteData);
            setShowConfirmation(true);
            setFormData(createEmptyWebsiteFormData());
            setCategorizationResetKey((prev) => prev + 1);

            setFieldErrors({ title: false, url: false, description: false });
            setShowValidation(false);

            toast.success(t("websiteManagement.successAddTitle"));
          } catch {
            toast.error(t("common.genericError"));
          }
        },
      });
    } catch {
      toast.error(t("common.genericError"));
    }
  };

  const categorySummary =
    addedWebsite?.categoryPathNames && addedWebsite.categoryPathNames.length > 0
      ? addedWebsite.categoryPathNames.join(" > ")
      : addedWebsite?.category1Name;
  const regionSummary =
    addedWebsite?.regionPathNames && addedWebsite.regionPathNames.length > 0
      ? addedWebsite.regionPathNames.join(" > ")
      : addedWebsite?.region;

  return (
    <>
      <WebsiteFormCard
        title={formConfig.card.title}
        description={formConfig.card.description}
        onSubmit={handleAddWebsite}
        formFields={
          <WebsiteFormFields
            formData={formData}
            setFormData={setFormData}
            t={t}
            language={language}
            isSubmitting={isSubmitting}
            showValidation={showValidation}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            urlErrorKey={urlErrorKey}
            onUrlChange={() => setUrlErrorKey(null)}
            maxTitleLen={WEBSITE_FORM_LIMITS.title}
            maxUrlLen={WEBSITE_FORM_LIMITS.url}
            maxDescriptionLen={WEBSITE_FORM_LIMITS.description}
            maxAddressLen={WEBSITE_FORM_LIMITS.address}
            maxZipLen={WEBSITE_FORM_LIMITS.zip}
            primaryTextStyle={primaryTextStyle}
            secondaryTextStyle={secondaryTextStyle}
            errorTextStyle={errorTextStyle}
            iconStyle={iconStyle}
            aiFeatureEnabled={aiFeatureEnabled}
            showCategoryRequiredError={categoryMissing}
            showRegionRequiredError={regionMissing}
            resetKey={categorizationResetKey}
            {...formConfig.fields}
          />
        }
        submitLabel={formConfig.card.submitLabel}
        submitIcon={formConfig.card.submitIcon}
        cancelLabel={formConfig.card.cancelLabel}
        onCancel={() => navigate("home")}
        isSubmitting={isSubmitting}
        primaryTextStyle={primaryTextStyle}
        secondaryTextStyle={secondaryTextStyle}
        actionsClassName={formConfig.card.actionsClassName}
      />

      <WebsiteFormConfirmationDialog
        open={showConfirmation}
        onOpenChange={setShowConfirmation}
        title={t("websiteManagement.successAddTitle")}
        icon={<CheckCircle className="w-6 h-6" style={iconStyle} />}
        iconContainerStyle={successIconStyle}
        description={
          <div className="space-y-3 pt-4">
            <p>{t("websiteManagement.successAddMessage")}</p>
            {addedWebsite && (
              <div
                className="border rounded-lg p-4 space-y-2 text-left"
                style={summaryCardStyle}
              >
                <SummaryEntry
                  label={t("websiteManagement.website")}
                  value={formatSummaryValue(addedWebsite.title)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteManagement.urlField")}
                  value={formatSummaryValue(addedWebsite.url)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteManagement.descriptionLabel")}
                  value={formatSummaryValue(addedWebsite.description)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteManagement.addressLabel")}
                  value={formatSummaryValue(addedWebsite.address ?? undefined)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteManagement.zipCodeLabel")}
                  value={formatSummaryValue(addedWebsite.zipCode)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteManagement.regionLabel")}
                  value={formatSummaryValue(regionSummary)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteManagement.category")}
                  value={formatSummaryValue(categorySummary)}
                  valueStyle={primaryTextStyle}
                />
              </div>
            )}
          </div>
        }
        primaryAction={{
          label: t("websiteSuggest.understood"),
          onClick: () => setShowConfirmation(false),
        }}
        secondaryAction={{
          label: t("websiteManagement.cancel"),
          onClick: () => setShowConfirmation(false),
          variant: "outline",
        }}
      />
    </>
  );
}
